<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleModal from '../EditTitleModal.vue';
import EditDescriptionModal from '../EditDescriptionModal.vue';

const b = bem('info-contact-module');

const DEFAULT_CONTACT_ITEMS = [
  { id: 1, name: 'EMAIL:', value: 'hello@example.com' },
  { id: 2, name: 'PHONE:', value: '800-000-0000' },
];

const DEFAULT_HOURS_ITEMS = [
  { id: 1, name: 'MONDAY-FRIDAY:', value: '9 am - 5 pm EST' },
];

export default {
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      isTitleModalVisible: false,
      isDescriptionModalVisible: false,
      isContactTitleModalVisible: false,
      isHoursTitleModalVisible: false,
      isContactItemModalVisible: false,
      isHoursItemModalVisible: false,
      activeItemIndex: null,
      activeSection: null,
    };
  },
  computed: {
    content() {
      if (this.moduleData?.parsedContent) {
        return this.moduleData.parsedContent;
      }
      if (!this.moduleData?.content) return {};
      if (typeof this.moduleData.content === 'string') {
        try {
          return JSON.parse(this.moduleData.content);
        } catch {
          return {};
        }
      }
      return this.moduleData.content;
    },
    displayTitle() {
      if (this.isMobile && this.content.titleMobile) {
        return this.content.titleMobile;
      }
      return this.content.title || 'Section Title';
    },
    displayDescription() {
      if (this.isMobile && this.content.descriptionMobile) {
        return this.content.descriptionMobile;
      }
      return this.content.description || 'Section Description';
    },
    contactTitle() {
      return this.content.contactTitle || 'Contact Info';
    },
    hoursTitle() {
      return this.content.hoursTitle || 'Hours of Operation';
    },
    contactItems() {
      return this.content.contactItems && this.content.contactItems.length > 0
        ? this.content.contactItems
        : DEFAULT_CONTACT_ITEMS;
    },
    hoursItems() {
      return this.content.hoursItems && this.content.hoursItems.length > 0
        ? this.content.hoursItems
        : DEFAULT_HOURS_ITEMS;
    },
    activeItem() {
      if (this.activeItemIndex === null || !this.activeSection) return null;
      const items = this.activeSection === 'contact' ? this.contactItems : this.hoursItems;
      return items[this.activeItemIndex] || null;
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    openTitleModal() {
      if (this.isEditMode) {
        this.isTitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (this.isEditMode) {
        this.isDescriptionModalVisible = true;
      }
    },
    openContactTitleModal() {
      if (this.isEditMode) {
        this.isContactTitleModalVisible = true;
      }
    },
    openHoursTitleModal() {
      if (this.isEditMode) {
        this.isHoursTitleModalVisible = true;
      }
    },
    openContactItemModal(index) {
      if (this.isEditMode) {
        this.activeItemIndex = index;
        this.activeSection = 'contact';
        this.isContactItemModalVisible = true;
      }
    },
    openHoursItemModal(index) {
      if (this.isEditMode) {
        this.activeItemIndex = index;
        this.activeSection = 'hours';
        this.isHoursItemModalVisible = true;
      }
    },
    handleTitleSave(data) {
      this.updateContent({
        ...this.content,
        title: data.title,
        titleMobile: data.titleMobile,
      });
      this.isTitleModalVisible = false;
    },
    handleDescriptionSave(data) {
      this.updateContent({
        ...this.content,
        description: data.description,
        descriptionMobile: data.descriptionMobile,
      });
      this.isDescriptionModalVisible = false;
    },
    handleContactTitleSave(data) {
      this.updateContent({
        ...this.content,
        contactTitle: data.title,
      });
      this.isContactTitleModalVisible = false;
    },
    handleHoursTitleSave(data) {
      this.updateContent({
        ...this.content,
        hoursTitle: data.title,
      });
      this.isHoursTitleModalVisible = false;
    },
    handleContactItemSave(data) {
      const updatedItems = [...this.contactItems];
      updatedItems[this.activeItemIndex] = {
        ...updatedItems[this.activeItemIndex],
        name: data.title,
        value: data.titleMobile || updatedItems[this.activeItemIndex].value,
      };
      this.updateContent({
        ...this.content,
        contactItems: updatedItems,
      });
      this.isContactItemModalVisible = false;
      this.activeItemIndex = null;
      this.activeSection = null;
    },
    handleHoursItemSave(data) {
      const updatedItems = [...this.hoursItems];
      updatedItems[this.activeItemIndex] = {
        ...updatedItems[this.activeItemIndex],
        name: data.title,
        value: data.titleMobile || updatedItems[this.activeItemIndex].value,
      };
      this.updateContent({
        ...this.content,
        hoursItems: updatedItems,
      });
      this.isHoursItemModalVisible = false;
      this.activeItemIndex = null;
      this.activeSection = null;
    },
    updateContent(newContent) {
      this.$emit('update', {
        ...this.moduleData,
        content: JSON.stringify(newContent),
      });
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, popup: isPopup })">
    <div
      :class="b('title-row', { editable: isEditMode })"
      role="button"
      tabindex="0"
      @click="openTitleModal"
      @keydown.enter="openTitleModal"
    >
      <span :class="b('title', { mobile: isMobile })">{{ displayTitle }}</span>
      <PencilIcon v-if="isEditMode" :size="20" color="#242424" :class="b('edit-icon')" />
    </div>

    <div
      :class="b('desc-row', { editable: isEditMode })"
      role="button"
      tabindex="0"
      @click="openDescriptionModal"
      @keydown.enter="openDescriptionModal"
    >
      <span :class="b('desc', { mobile: isMobile, popup: isPopup })">{{ displayDescription }}</span>
      <PencilIcon v-if="isEditMode" :size="16" color="#595959" :class="b('edit-icon')" />
    </div>

    <div :class="b('info-wrapper', { mobile: isMobile, popup: isPopup })">
      <div :class="b('info-block', { mobile: isMobile, popup: isPopup, editable: isEditMode })">
        <div :class="b('info-title-row', { editable: isEditMode })">
          <span :class="b('info-title', { mobile: isMobile })">{{ contactTitle }}</span>
        </div>
        <div
          v-for="(item, index) in contactItems"
          :key="item.id || index"
          :class="b('info-item', { editable: isEditMode, mobile: isMobile })"
          role="button"
          tabindex="0"
          @click="openContactItemModal(index)"
          @keydown.enter="openContactItemModal(index)"
        >
          <span :class="b('info-name', { mobile: isMobile })">{{ item.name }}</span>
          <span :class="b('info-value', { mobile: isMobile })">{{ item.value }}</span>
          <PencilIcon v-if="isEditMode" :size="20" color="#595959" :class="b('edit-icon')" />
        </div>
      </div>

      <div :class="b('info-block', { mobile: isMobile, popup: isPopup, editable: isEditMode })">
        <div :class="b('info-title-row', { editable: isEditMode })">
          <span :class="b('info-title', { mobile: isMobile })">{{ hoursTitle }}</span>
        </div>
        <div
          v-for="(item, index) in hoursItems"
          :key="item.id || index"
          :class="b('info-item', { editable: isEditMode, mobile: isMobile })"
          role="button"
          tabindex="0"
          @click="openHoursItemModal(index)"
          @keydown.enter="openHoursItemModal(index)"
        >
          <span :class="b('info-name', { mobile: isMobile })">{{ item.name }}</span>
          <span :class="b('info-value', { mobile: isMobile })">{{ item.value }}</span>
          <PencilIcon v-if="isEditMode" :size="20" color="#595959" :class="b('edit-icon')" />
        </div>
      </div>
    </div>

    <EditTitleModal
      :visible="isTitleModalVisible"
      :title="content.title"
      :title-mobile="content.titleMobile"
      @close="isTitleModalVisible = false"
      @save="handleTitleSave"
    />

    <EditDescriptionModal
      :visible="isDescriptionModalVisible"
      :description="content.description"
      :description-mobile="content.descriptionMobile"
      @close="isDescriptionModalVisible = false"
      @save="handleDescriptionSave"
    />

    <EditTitleModal
      :visible="isContactTitleModalVisible"
      :title="content.contactTitle"
      :title-mobile="''"
      modal-title="Edit Section Title"
      first-field-label="Title"
      first-field-placeholder="e.g. Contact Info"
      :hide-second-field="true"
      @close="isContactTitleModalVisible = false"
      @save="handleContactTitleSave"
    />

    <EditTitleModal
      :visible="isHoursTitleModalVisible"
      :title="content.hoursTitle"
      :title-mobile="''"
      modal-title="Edit Section Title"
      first-field-label="Title"
      first-field-placeholder="e.g. Hours of Operation"
      :hide-second-field="true"
      @close="isHoursTitleModalVisible = false"
      @save="handleHoursTitleSave"
    />

    <EditTitleModal
      :visible="isContactItemModalVisible"
      :title="activeItem?.name"
      :title-mobile="activeItem?.value"
      modal-title="Edit Contact"
      first-field-label="Label"
      first-field-placeholder="e.g. EMAIL:"
      second-field-label="Value"
      second-field-placeholder="e.g. hello@example.com"
      @close="isContactItemModalVisible = false; activeItemIndex = null; activeSection = null"
      @save="handleContactItemSave"
    />

    <EditTitleModal
      :visible="isHoursItemModalVisible"
      :title="activeItem?.name"
      :title-mobile="activeItem?.value"
      modal-title="Edit Hours"
      first-field-label="Day(s)"
      first-field-placeholder="e.g. MONDAY-FRIDAY:"
      second-field-label="Time"
      second-field-placeholder="e.g. 9 am - 5 pm EST"
      @close="isHoursItemModalVisible = false; activeItemIndex = null; activeSection = null"
      @save="handleHoursItemSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.info-contact-module {
  width: 1540px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 64px 0;

  @include mediaMinMax(1025, $normal) {
    width: vw-full(1540);
    padding: vw-full(64) 0;
  }
  &_popup {
    width: 1200px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1200);
    }
  }
  &_mobile {
    width: 412px;
    padding: 40px 0;
  }

  &__title-row,
  &__desc-row {
    display: flex;
    align-items: center;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin: -5px -10px;
      border-radius: 5px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 40px;
    font-weight: 600;
    color: #293241;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }
    &_mobile {
      font-size: 24px;
    }
  }

  &__desc {
    font-family: Montserrat, sans-serif;
    font-size: 19px;
    font-weight: 400;
    color: #5e5d5d;
    text-align: center;
    margin-top: 24px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
      margin-top: vw-full(24);
    }
    &_popup {
      margin-top: 12px;

      @include mediaMinMax(1025, $normal) {
        margin-top: vw-full(12);
      }
    }
    &_mobile {
      font-size: 16px;
      margin-top: 20px;
    }
  }

  &__edit-icon {
    flex-shrink: 0;
  }

  &__info-wrapper {
    margin-top: 82px;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    @include mediaMinMax(1025, $normal) {
      margin-top: vw-full(82);
    }
    &_popup {
      margin-top: 64px;

      @include mediaMinMax(1025, $normal) {
        margin-top: vw-full(64);
      }
    }
    &_mobile {
      flex-direction: column;
      gap: 40px;
      margin-top: 40px;
      width: 100%;
    }
  }

  &__info-block {
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 21px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(21);
    }

    &:first-child {
      border-right: solid 1px #293241;
    }

    &:last-child {
      padding-left: 225px;

      @include mediaMinMax(1025, $normal) {
        padding-left: vw-full(225);
      }
    }

    &_editable:last-child {
      padding-left: 195px;

      @include mediaMinMax(1025, $normal) {
        padding-left: vw-full(195);
      }
    }
    &_popup {
      gap: 16px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(16);
      }

      &:last-child {
        padding-left: 175px;

        @include mediaMinMax(1025, $normal) {
          padding-left: vw-full(175);
        }
      }
    }
    &_mobile {
      min-width: initial;
      width: 100%;
      align-items: center;
      border-right: none;
      padding-left: 0;
      &:first-child {
        border-right: none;
      }
      &:last-child {
        padding-left: 0
      }
    }
  }

  &__info-title-row {
    display: flex;
    align-items: center;
    gap: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(8);
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin: -5px -10px;
      border-radius: 5px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }

  &__info-title {
    font-family: Montserrat, sans-serif;
    font-size: 36px;
    font-weight: 500;
    color: #293241;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(36);
    }
    &_mobile {
      font-size: 24px;
    }
  }

  &__info-item {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: baseline;
    gap: 10px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(10);
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin: -5px -10px;
      border-radius: 5px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
    &_mobile {
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }
  }

  &__info-name {
    color: #293241;
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
    &_mobile {
      font-size: 16px;
    }
  }

  &__info-value {
    color: #5e5d5d;
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 400;
    line-height: normal;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
    &_mobile {
      font-size: 16px;
    }
  }

  &__divider {
    display: none;
    &_mobile {
      display: block;
      width: 100%;
      height: 1px;
      background: #293241;
    }
  }
}
</style>
